import { LitElement, html, css } from 'lit';
import {msg,str} from '@lit/localize';
import { accessibility,formStyles,dialog,markdown  } from '../../utilities/styles';
import { cardLinks } from '../../utilities/accessibility';
import { EventManager } from '../../utilities/events';
import { editIcon, deleteIcon } from '../../utilities/icons';
import { cache } from 'lit/directives/cache.js';
import { ContextConsumer } from '@lit/context';
import { organisationContext } from '../../utilities/context';
import { validateForm, lockForm, checkFormLock, releaseFormLock, hideRevealInputs, removeHideRevealInputs } from '../../utilities/formsUtilities';
import { AllInConditionals } from '../Utilities/AllInConditionals';
import { handle403s } from '../../utilities/AllInErrorHandling';

export class AllInOrganisationAccountForm extends AllInConditionals(LitElement) {
    static properties = {
        currentOrg: {type: String, reflect:true},
        name: { type: String },
        _showForm: { state:true,type: Boolean },
        _accData: { state:true,type: Array },
        _isFormLocked: {state:true,type:Boolean},
    }

    constructor() {
        super();
    }

    get _form(){
        return this.shadowRoot.querySelector('form');
    }

    get _modal(){
        return this.shadowRoot.querySelector('dialog');
    }

    async connectedCallback() {
        super.connectedCallback();
        this._organisationData = new ContextConsumer(this, {context:organisationContext,subscribe:true});
        this.organisationData = this._organisationData?.value;
        ({currentOrg:this.currentOrg,name:this.name,restUrl:this.restUrl,organisations:this.organisations,action:this.action,nonce:this.nonce,user:this.currentUser} = this.organisationData);
        this.organisation = this._getOrganisationById(this.currentOrg);
        cardLinks(this.shadowRoot);
        this.eventManager = new EventManager(this);
        this._setAccData();
        this._showForm = false;
        //this.renderRoot.querySelector('a')?.focus();
        this.formID = `${this.currentOrg}-account-form`;
        this.uid = `account-form-${this.organisationData.user.id}`;
        if(this.organisation.hasOwnProperty('users')){
            this._userData = this.organisation.users;
            this._loading = false;
        }
        else{
           await this._fetchUsers();
        }
        this._role = this._userData.users.find(user => user.id === this.currentUser.id)?.role;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        releaseFormLock(this.formID,this.uid,this.restUrl,this.nonce);

    }

    async firstUpdated(){
        this._isFormLocked = await checkFormLock(this.formID,this.uid,this.restUrl,this.nonce);
    }

    updated(changedProperties){
        if(changedProperties.has('_showForm')){
            if(this._showForm==true){
                if(this.isFormLocked) return;
                lockForm(this.formID,this.uid,this.restUrl,this.nonce);
                this.renderRoot.querySelector('input')?.focus();
                let urlInputs = this._form?.querySelectorAll('input[type="url"]');
                urlInputs?.forEach(input=>{
                    input.addEventListener('blur',this._slotURLChange.bind(this));
                });
                hideRevealInputs(this._form);
            }
            else{
                this.renderRoot.querySelector('a')?.focus();
                removeHideRevealInputs(this._form);
            }
        }
    }

    render() {
        let main='';
        
        if(this._showForm){
            if(this._isFormLocked){
                main = html`
                    <h3>${msg(`Organisation account details`)}</h3>
                    <div class="bg">
                        <div class="message notice" aria-live="polite">
                            <p>${msg('This form is currently being edited by another member of this organisation')}</p>
                        </div>
                    </div>
                `;
            }    
            else{
                let data = this._draftAccData.length>0?this._draftAccData:this._accData;
                main=html`
                    <h3>${msg(`Edit organisation account details`)}</h3>
                    <form @submit=${this._handleModal}>
                        <div class="message notice" aria-live="polite">
                            <p>${msg('Any edits will need to be reviewed and approved')}</p>
                        </div>
                        <div class="bg">
                                ${data.map((item)=>{
                                    if(item.type=='file'){
                                    return html`
                                        <div class="input-group">
                                            <allin-image-uploader inputname="org_logo" labelalt="${msg('Organisation logo')}" label ="${msg('Upload an organisation logo')}" src="${item.value}">
                                            </allin-image-uploader>
                                        </div>`;
                                    }
                                    if(item.type=='select'){
                                        if(item.dependency && item.dependency.input && item.dependency.value){
                                            return html`
                                                <div class="input-group">
                                                    <label for="${item.name}">${item.title}</label>
                                                    <select id="${item.name}" name="${item.name}" data-hider='${item.dependency.input}' data-reveal='${item.dependency.value}'>
                                                    ${
                                                        item.options.map((option)=>{
                                                            return html`
                                                                <option value="${option.value}" ?selected=${option.value==item.value}>${option.label}</option>
                                                            `;
                                                        })
                                                        }
                                                    </select>
                                                </div>
                                            `;
                                        }
                                        return html`
                                            <div class="input-group">
                                                <label for="${item.name}">${item.title}</label>
                                                <select id="${item.name}" name="${item.name}">
                                                   ${
                                                    item.options.map((option)=>{
                                                        return html`
                                                            <option value="${option.value}" ?selected=${option.value==item.value}>${option.label}</option>
                                                        `;
                                                    })
                                                    }
                                                </select>
                                            </div>
                                        `;
                                    }
                                    if(item.dependent){
                                    return html`
                                        <div class="input-group hidden ${item.name}">
                                            <label for="${item.name}">${item.title}</label>
                                            <input type="${item.type}" id="${item.name}" name="${item.name}" value=${item.value} />
                                        </div>
                                    `
                                    }
                                    if(item.type=='textarea'){
                                        return html`
                                            <div class="input-group">
                                                <label for="${item.name}">${item.title}</label>
                                                <allin-markdown-editor name="${item.name}" id="${item.name}" data-label="${item.title}" labelalt="About" placeholder="${msg(`Description`)}" value="${item.value}" required></allin-markdown-editor>
                                            </div>
                                        `;
                                    }
                                    return html`
                                    <div class="input-group">
                                        <label for="${item.name}">${item.title}</label>
                                        <input type="${item.type}" id="${item.name}" name="${item.name}" ?aria-required="${item.required}" value=${item.value} />
                                    </div>
                                `
                                })} 
                                <input type="hidden" name="org_id" value="${this.currentOrg}" />
                                <input type="hidden" name="action" value="${this.action}-account" />
                                <div class="button-group">
                                    <button type="submit">${msg(`Save changes and submit for approval`)}</button>
                                </div>   
                                <hr>
                                <a href="javascript:void(0)" @click=${this._handleClick}>${deleteIcon}${msg(`Cancel`)}</a>
                        </div>
                    </form> 
                    <dialog>
                        <button class="close" autofocus @click=${this._handleModal}><span aria-hidden="true">${deleteIcon}</span>${msg(`Cancel`)}</button>
                        <p>
                            ${msg(`Thank you for submitting your changes for approval. You can still make and preview changes.`)}
                        </p>
                        <button @click=${this._handleSubmit}>${msg(`Submit for approval`)}</button>
                    </dialog>
                `;
            }
        }
        else{
            let cta = html`<hr><a href="javascript:void(0)" @click=${this._handleClick}>${editIcon}${msg(`Edit these details`)}</a>`; 
            let subHead = '';
            if(this.organisation.approvalStatus=='AwaitingResponse'||this.organisation.cultural_profile?.approvalStatus=='AwaitingResponse'){
                cta = html`
                    <hr>
                    <div class="message notice" aria-live="polite">
                        <p>${msg('You are waiting for approval on your recent change. Please note that your most recent changes might take approximately 30 minutes to show.')}</p>
                    </div>
                    <a href="javascript:void(0)" @click=${this._handleClick}>${editIcon}${msg(`Edit submission`)}</a>
                `
            }
            else if(this.organisation.approvalStatus=='NewApplication'){
                cta =html`<p><strong>${msg('Your application is being reviewed')}</strong></p>`;
            }
            else if(this.organisation.approvalStatus=='ChangesRequested'){
                cta = html`
                    <hr>
                    <div class="message error" aria-live="polite">
                        <p>${msg('Changes were requested on your recent submission. Please edit and resubmit.')}</p>
                        <h4>${msg('Changes:')}</h4>
                        <p>${this.organisation.declineMessage}</p>
                    </div>
                    <a href="javascript:void(0)" @click=${this._handleClick}>${editIcon}${msg(`Edit submission`)}</a>
                `;
                subHead = html`<p>${msg('Changes were requested on your recent submission. Please edit and resubmit.')}</p>`;

            }
            if(!this._role=='ADMIN'&&!this._role=='MANAGER'){
                cta = '';
            }
            let data = this._draftAccData.length>0?this._draftAccData:this._accData;
            main = html`
                <h3>${msg(`Organisation account details`)}</h3>
                ${subHead}
                <div class="bg">
                        <table>
                            <thead class="screen-reader-text">
                                <tr>
                                    <th>Field</th>
                                    <th>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${data.map((item)=>{
                                    if(item.type=='file'){
                                        return html`
                                            <tr class="field">
                                                <td>${item.title}</td>
                                                <td><img src="${item.value}" alt="Organisation logo" width="200px" height="200px" style="object-fit:contain" /></td>
                                            </tr>
                                        `;
                                    }
                                    if(item.dependent){
                                        let dependency = data.find((i)=>i.name==item.dependent.input);
                                        if(dependency && dependency.value!=dependency.dependency.value){
                                            return '';
                                        }
                                    }
                                    let value = item.value;
                                    if(item.options){
                                        value = item.options.find((i)=>i.value==item.value)?.label;
                                    }
                                    if(item.name=='description' && value){
                                        value = html`<allin-markdown-editor readonly value="${value}"></allin-markdown-editor>`;
                                    }
                                    return html`
                                    <tr class="field">
                                        <td>${item.title}</td>
                                        <td>${value}</td>
                                    </tr>
                                `
                                })}    
                            </tbody>              
                        </table>
                        ${cta}   
                </div>
            `;
        }
        return html`${cache(main)}`;
    }

    _handleClick(e){
        e.preventDefault();
        this._showForm = !this._showForm;
    }

    _handleModal(e){
        e.preventDefault();
        if(this._modal.open){
            this._modal.close();
        }
        else{
            this._modal.showModal();
        }
    }

    async _handleSubmit(e){
        e.preventDefault();
        this._modal.close();
        let formResponse = this._form.querySelector('.message');
        formResponse.innerHTML = '';
        formResponse.classList.remove('error');
        let submitButton = this._form.querySelector('[type="submit"]');
        let valid = validateForm(this._form,formResponse);
        if(!valid) return;
        let formData = new FormData(this._form);
        //formData = JSON.stringify(Object.fromEntries(formData));
        this._form.classList.add('loading');
        submitButton.disabled = true;
        this._form.setAttribute('aria-busy', 'true');
        try{
            let response = await fetch(this.restUrl, {
                method: 'POST',
                body: formData,
                headers: {
                    'AllIn-Nonce': this.nonce,
                }
            })

            if(response.status === 403){
                handle403s();
                return;
            }

            response = await response.json();
            if(response.ok){
                this._form.classList.remove('loading');
                this._form.setAttribute('aria-busy', 'false');
                this._form.reset();
                this.eventManager.emit(`allin-organisation-${this.name}-update`,{organisation:response.data});
                let org = JSON.parse(response.data);
                this.organisation = org;
                this._setAccData();
                submitButton.disabled = false; 
                this._showForm = false;
                releaseFormLock(this.formID,this.uid,this.restUrl,this.nonce);
            }
            else{
                formResponse.innerHTML = response.message;
                formResponse.classList.add('error');
                this._form.classList.remove('loading');
                this._form.setAttribute('aria-busy', 'false');
                submitButton.disabled = false; 
            }
        }
        catch(error){
            formResponse.innerHTML = 'Something went wrong';
            formResponse.classList.add('error');
            this._form?.classList.remove('loading');
            this._form?.setAttribute('aria-busy', 'false');
        };
    }

    _getOrganisationById(id){
        return this.organisations.find((org)=>org.org_id==id);
    }

    _setAccData(){
        let cultural_profile = this.organisation.profiles.filter((profile)=>profile.type=='cultural');
        if(cultural_profile){
            cultural_profile = cultural_profile[0];
            if(!cultural_profile){
                cultural_profile = {};
            }
            cultural_profile.draftInfo = cultural_profile.draftInfo?cultural_profile.draftInfo:{};
            if(typeof cultural_profile.draftInfo==='string'){
                cultural_profile.draftInfo = JSON.parse(cultural_profile.draftInfo);
            }
        }
        if(!this.organisation.homeCountry){
            this.organisation.homeCountry = 'England';
        }
        if(cultural_profile.ticketingSystem&&cultural_profile.ticketingSystem==='TicketsolveJSON'){
            cultural_profile.ticketingSystem = 'Ticketsolve';
        }
        this._accData = [
            {name:'name',type:'text',title:msg(str`Organisation name`),value:this.organisation.name,required:true},
            {name:'email',type:'email',title:msg(str`Organisation email`),value:this.organisation.email,required:true},
            {name:'website',type:'url',title:msg(str`Organisation website`),value:this.organisation.website},
            {name:'address1',type:'text',title:msg(str`Organisation postal address`),value:this.organisation.address1,required:true},
            {name:'address2',type:'text',title:msg(str`Organisation postal address line 2`),value:this.organisation.address2},
            {name:'city',type:'text',title:msg(str`City`),value:this.organisation.city,required:true},
            {name:'homeCountry',type:'select',title:msg(str`Nation`),value:this.organisation.homeCountry,options:[
                    {label: 'England', value: 'England'},
                    {label: 'Scotland', value: 'Scotland'},
                    {label: 'Wales', value: 'Wales'},
                    {label: 'Northern Ireland', value: 'NorthernIreland'},
                    {label: 'Ireland', value: 'Ireland'},
            ]},
            {name:'country',type:'text',title:msg(str`Country`),value:this.organisation.country,required:true},
            {name:'postcode',type:'text',title:msg(str`Postcode`),value:this.organisation.postcode,required:true},
            {name:'locationCount',type:'number',title:msg(str`Number of locations`),value:this.organisation.locationCount,required:true},
            {name:'phone',type:'text',title:msg(str`Organisation phoneline`),value:this.organisation.phone},
            {name:'description',type:'textarea',title:msg(str`Organisation description`),value:cultural_profile?.description,required:true},
            cultural_profile?{name:'category',type:'select',title:msg(str`Organisation category`),value:cultural_profile?.category,options:[
                    {value: 'Circus', label: msg('Circus')},
                    {value: 'CombinedArts', label: msg('Combined Arts')},
                    {value: 'Comedy', label: msg('Comedy')},
                    {value: 'CommunitySpace', label: msg('Community Space')},
                    {value: 'Craft', label: msg('Craft')},
                    {value: 'Dance', label: msg('Dance')},
                    {value: 'Festival', label: msg('Festival')},
                    {value: 'Educational', label: msg('Educational')},
                    {value: 'FilmCinema', label: msg('Film/Cinema')},
                    {value: 'Gallery', label: msg('Gallery')},
                    {value: 'ImmersiveAndVR', label: msg('Immersive and VR')},
                    {value: 'Library', label: msg('Library')},
                    {value: 'Museum', label: msg('Museum')},
                    {value: 'Music', label: msg('Music')},
                    {value: 'Theatre', label: msg('Theatre')},
                    {value: 'VisitorAttraction', label: msg('Visitor Attraction')},
                    {value: 'VisualArt', label: msg('Visual Art')},
                    {value: 'FamilyFriendly', label: msg('Family Friendly')},
                    {value: 'LiteraturePoetry', label: msg('Literature and Poetry')}
            ]}:null,
            //{name:'userAdminJobTitle',type:'text',title:msg(str`Primary admin job title`),value:this.organisation.userJobTitle},
            {name:'logo',type:'file',title:msg(str`Organisation logo`),value:this.organisation.fileURL},
            {name:'altText',type:'text',title:msg(str`Organisation logo alt text`),value:this.organisation.photoAltText},
            {name:'contactPreferences',type:'select',title:msg(str`Contact preferences`),value:this.organisation.contactPreferences,options:[
                {label:msg('Email'),value:'Email'},
                {label:msg('Phone'),value:'Phone'},
            ]},
            {name:'ticketingSystem',type:'select',title:msg(str`Ticketing system`),value:cultural_profile.ticketingSystem,options:[
                    {label:'Tessitura',value:'Tessitura'},
                    {label:'Ticketsolve',value:'Ticketsolve'},
                    {label:'Spektrix',value:'Spektrix'},
                    {label:'LineUp',value:'LineUp'},
                    {label:'Other',value:'Other'},
                    {label:'None',value:'None'}
                ],
                dependency:{input:'ticketingSystemOther',value:'Other'}
            },
            {name:'ticketingSystemOther',type:'text',title:msg(str`Ticketing system other`),value:cultural_profile.ticketingSystemOther,dependent:{input:'ticketingSystem'}},
            {name:'analysisTool',type:'select',title:msg(str`Analysis tool`),value:cultural_profile.analysisTool,options:[
                    {label:msg('Audience Finder'),value:'AudienceFinder'},
                    {label:msg('Illuminate'),value:'Illuminate'},
                    {label:msg('Looker Studio'),value:'LookerStudio'},
                    {label:msg('Power BI'),value:'PowerBI'},
                    {label:msg('Tableau'),value:'Tableau'},
                    {label:msg('Other'),value:'Other'},
                    {label:msg('None'),value:'None'}
                ],
                dependency:{input:'analysisToolOther',value:'Other'}
            },
            {name:'analysisToolOther',type:'text',title:msg(str`Analysis tool other`),value:cultural_profile.analysisToolOther,dependent:{input:'analysisTool'}},
            {name:'turnover',type:'select',title:msg(str`Annual turnover`),value:this.organisation.turnover,options:[
                {label:msg('Up to £500k'),value:'UpTo500k'},
                {label:msg('£500k - £1m'),value:'Between500kAnd1m'},
                {label:msg('£1m - £2m'),value:'Between1mAnd2m'},
                {label:msg('£2m - £5m'),value:'Between2mAnd5m'},
                {label:msg('£5m - £10m'),value:'Between5mAnd10m'},
                {label:msg('£10m+'),value:'Over10m'},
            ]},
        ];
        this._accData = this._accData.filter(item=>item.value!=null);
        let hasDraft = this.organisation.hasOwnProperty('draftInfo') && this.organisation.draftInfo && this.organisation.approvalStatus=='AwaitingResponse';
        if(!hasDraft && cultural_profile){
            hasDraft = cultural_profile.hasOwnProperty('draftInfo') && Object.keys(cultural_profile.draftInfo).length && this.organisation.approvalStatus=='AwaitingResponse';
        }
        if(hasDraft){
            if(cultural_profile.draftInfo.ticketingSystem&&cultural_profile.draftInfo.ticketingSystem==='TicketsolveJSON'){
                cultural_profile.draftInfo.ticketingSystem = 'Ticketsolve';
            }
            this._draftAccData = [
                {name:'name',type:'text',title:msg(str`Draft organisation name`),value:this.organisation.draftInfo.name??this.organisation.name,required:true},
                {name:'email',type:'email',title:msg(str`Draft organisation email`),value:this.organisation.draftInfo.email??this.organisation.email,required:true},
                {name:'website',type:'url',title:msg(str`Organisation website`),value:this.organisation.draftInfo.website??this.organisation.website},
                {name:'address1',type:'text',title:msg(str`Organisation postal address`),value:this.organisation.draftInfo.address1??this.organisation.address1,required:true},
                {name:'address2',type:'text',title:msg(str`Organisation postal address line 2`),value:this.organisation.draftInfo.address2??this.organisation.address2},
                {name:'city',type:'text',title:msg(str`City`),value:this.organisation.draftInfo.city??this.organisation.city,required:true},
                {name:'homeCountry',type:'select',title:msg(str`Nation`),value:this.organisation.draftInfo.homeCountry??this.organisation.homeCountry, options:[
                        {value: 'England', label: msg('England')},
                        {value: 'Scotland', label: msg('Scotland')},
                        {value: 'Wales', label: msg('Wales')},
                        {value: 'NorthernIreland', label: msg('Northern Ireland')},
                        {value: 'Ireland', label: msg('Ireland')},
                ]},
                {name:'country',type:'text',title:msg(str`Country`),value:this.organisation.draftInfo.country??this.organisation.country,required:true},
                {name:'postcode',type:'text',title:msg(str`Postcode`),value:this.organisation.draftInfo.postcode??this.organisation.postcode,required:true},
                {name:'locationCount',type:'number',title:msg(str`Number of locations`),value:this.organisation.draftInfo.locationCount??this.organisation.locationCount},
                {name:'phone',type:'text',title:msg(str`Organisation phoneline`),value:this.organisation.draftInfo.phone??this.organisation.phone},
                {name:'description',type:'textarea',title:msg(str`Organisation description`),value:cultural_profile?.draftInfo?.description??cultural_profile?.description,required:true},
                cultural_profile?{name:'category',type:'select',title:msg(str`Organisation category`),value:cultural_profile?.draftInfo?.category??cultural_profile?.description,required:true,options:[
                    {value: 'Circus', label: msg('Circus')},
                    {value: 'CombinedArts', label: msg('Combined Arts')},
                    {value: 'Comedy', label: msg('Comedy')},
                    {value: 'CommunitySpace', label: msg('Community Space')},
                    {value: 'Craft', label: msg('Craft')},
                    {value: 'Dance', label: msg('Dance')},
                    {value: 'Festival', label: msg('Festival')},
                    {value: 'Educational', label: msg('Educational')},
                    {value: 'FilmCinema', label: msg('Film/Cinema')},
                    {value: 'Gallery', label: msg('Gallery')},
                    {value: 'ImmersiveAndVR', label: msg('Immersive and VR')},
                    {value: 'Library', label: msg('Library')},
                    {value: 'Museum', label: msg('Museum')},
                    {value: 'Music', label: msg('Music')},
                    {value: 'Theatre', label: msg('Theatre')},
                    {value: 'VisitorAttraction', label: msg('Visitor Attraction')},
                    {value: 'VisualArt', label: msg('Visual Art')},
                    {value: 'FamilyFriendly', label: msg('Family Friendly')},
                ]}:null,
                //{name:'userAdminJobTitle',type:'text',title:msg(str`Draft primary admin job title`),value:this.organisation.draftInfo.userJobTitle},
                {name:'logo',type:'file',title:msg(str`Organisation logo`),value:this.organisation.draftInfo.fileURL? this.organisation.draftInfo.fileURL : this.organisation.fileURL},
                {name:'altText',type:'text',title:msg(str`Organisation logo alt text`),value:this.organisation.draftInfo.photoAltText? this.organisation.draftInfo.photoAltText : this.organisation.photoAltText},
                {name:'contactPreferences',type:'select',title:msg(str`Draft contact preferences`),value:this.organisation.draftInfo.contactPreferences??this.organisation.contactPreferences,options:[
                    {label:'Email',value:'Email'},
                    {label:'Phone',value:'Phone'},
                ]},
                {name:'ticketingSystem',type:'select',title:msg(str`Draft ticketing system`),value:cultural_profile?.draftInfo?.ticketingSystem??cultural_profile?.ticketingSystem,options:[
                        {label:'Tessitura',value:'Tessitura'},
                        {label:'Ticketsolve',value:'Ticketsolve'},
                        {label:'Spektrix',value:'Spektrix'},
                        {label:'LineUp',value:'LineUp'},
                        {label:'Other',value:'Other'},
                        {label:'None',value:'None'}
                    ],
                    dependency:{input:'ticketingSystemOther',value:'Other'}
                },
                {name:'ticketingSystemOther',type:'text',title:msg(str`Draft ticketing system other`),value:cultural_profile?.draftInfo?.ticketingSystemOther??cultural_profile?.ticketingSystemOther,dependent:{input:'ticketingSystem'}},
                {name:'analysisTool',type:'select',title:msg(str`Draft analysis tool`),value:cultural_profile?.draftInfo?.analysisTool??cultural_profile?.analysisTool,options:[
                         {label:msg('Audience Finder'),value:'AudienceFinder'},
                        {label:msg('Illuminate'),value:'Illuminate'},
                        {label:msg('Looker Studio'),value:'LookerStudio'},
                        {label:msg('Power BI'),value:'PowerBI'},
                        {label:msg('Tableau'),value:'Tableau'},
                        {label:msg('Other'),value:'Other'},
                        {label:msg('None'),value:'None'}
                    ],
                    dependency:{input:'analysisToolOther',value:'Other'}
                },
                {name:'analysisToolOther',type:'text',title:msg(str`Draft analysis tool other`),value:cultural_profile?.draftInfo?.analysisToolOther??cultural_profile?.analysisToolOther,dependent:{input:'analysisTool'}},
                {name:'turnover',type:'select',title:msg(str`Annual turnover`),value:this.organisation.draftInfo.turnover??this.organisation.turnover,options:[
                    {label:msg('Up to £500k'),value:'UpTo500k'},
                    {label:msg('£500k - £1m'),value:'Between500kAnd1m'},
                    {label:msg('£1m - £2m'),value:'Between1mAnd2m'},
                    {label:msg('£2m - £5m'),value:'Between2mAnd5m'},
                    {label:msg('£5m - £10m'),value:'Between5mAnd10m'},
                    {label:msg('£10m+'),value:'Over10m'},
                ]},
            ];
            this._draftAccData = this._draftAccData.filter(item=>item.value!=undefined && item.value!=null);
        }
        else{
            this._draftAccData = [];
        }
    }

    _slotURLChange(e) {
        if(e.target.type=='url'){
            if(e.target.value=='') return;
            try{
                const currentUrl = new URL(e.target.value);
                const { protocol } = currentUrl;

                if (protocol !== 'http:' && protocol !== 'https:') {
                    currentUrl.protocol = 'https:';
                    e.target.value = currentUrl.toString();
                }
            }
            catch(error){
                e.target.value = `https://${e.target.value}`;
            }
        }
     }
     
    async _fetchUsers(){
        let url = `${this.restUrl}?org_id=${this.currentOrg}&action=${this.action}-users&nonce=${this.nonce}`;
        let response = await fetch(url,{
            headers: {
                'AllIn-Nonce': this.nonce,
            }
        });

        if(response.status === 403){
            handle403s();
            return;
        }

        response = await response.json();
        this._loading = false;
        this._userData = response.users;
        this.organisation.users = response.users;
        this.eventManager.emit(`allin-organisation-${this.name}-update`,{organisation:JSON.stringify(this.organisation),panel:'account'});
    }
    
    static styles = [
        accessibility,
        formStyles,
        dialog,
        markdown,
        css`
        allin-greeting{
            --allin-font-size: 2rem;
            --allin-font-weight: 700;
        }
        .bg{
            padding:var(--allin-padding,2rem);
            background-color:var(--allin-bg-color,#ccc);
            margin-bottom:0.5rem;
        }
        .bg a{
            font-size:0.9rem;
            text-decoration:none;
            font-weight:700;
            color:var(--allin-highlight-color,#472961);
            display:flex;
            align-items:center;
            gap:0.5rem;
        }

        .bg a:hover{
            text-decoration:underline;
        }



        .small{
            font-size:0.875rem;
        }

        fieldset{
            border:0;
            padding:0;
            margin:0;
        }

        legend{
            font-size:1.25rem;
            font-weight:700;
            margin-bottom:1rem;
            color:var(--allin-highlight-color,#472961);
        }
        legend:has(+p){
            margin-bottom:0.5rem;
        }

        .button-group{
            display:flex;
            justify-content:space-between;
            margin-top:2rem;
        }

        .border-top{
            border-top:1px solid var(--allin-highlight-color,#472961);
            padding-top:2rem;
        }
        .button-group button:not([type=submit]), .return{
            background-color:transparent;
            color:var(--allin-highlight-color,#472961);
            border:0;
            display:flex;
            gap:0.5rem;
            align-items:center;
            text-decoration:underline;
            cursor:pointer;
            transition:color var(--transition) ease;
            font-weight:700;
            font-size:1rem;

        }
        .return{
            padding:0;
            margin-bottom:2rem;
        }

        .button-group button svg, .return svg{
            transition:transform var(--transition) ease;
        }
        .button-group button:hover, .return:hover{
            text-decoration-thickness:2px;
        }
        
        .button-group button:hover svg, .return:hover svg{
            transform:rotate(15deg);
        }
        @media (prefers-reduced-motion: reduce){
            .button-group button svg, .return svg{
                transform:none !important;
            }
        }
        table{
            width:100%;
            border-collapse:collapse;
        }

        tr{
            display:grid;
        }
        td::first-child{
            padding-bottom:0.5rem;
        }
        td:last-child{
            font-weight:600;
            padding-bottom:2rem;
        }
        @media screen and (min-width:700px){
            tr{
                display:table-row
            }
            td:first-child{
                width:42%;
                padding-right:2rem;
                vertical-align:top;
                padding-bottom:2rem;
            }

            td:last-child{
                width:58%;
                padding-bottom:2rem;
                vertical-align:top;
            }
        }

        td{
            overflow-wrap:break-word;
        }

        
        
        td svg{
            height:0.5rem;
        }

        hr{
            border:0;
            border-top:1px solid var(--allin-highlight-color,#472961);
            margin-top:2rem;
            margin-bottom:2rem;
        }
        allin-image-uploader img[src='']{
            display:none;
        }

        allin-image-uploader img {
            width: 100px;
            height: 100px;
            object-fit: contain;
            object-position: left;
        }

        `
        ];
}